import React, { useEffect, useState } from "react";
import { api, formatRupiah, formatApiError } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, X, CheckSquare, XSquare } from "lucide-react";
import { toast } from "sonner";

export default function Approval() {
  const [data, setData] = useState({ kasbon: [], purchasing: [] });
  const [selKasbon, setSelKasbon] = useState(new Set());
  const [selPO, setSelPO] = useState(new Set());

  const load = async () => {
    const { data } = await api.get("/approvals/pending");
    setData(data);
    setSelKasbon(new Set());
    setSelPO(new Set());
  };
  useEffect(() => { load(); }, []);

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };
  const toggleAll = (list, set, setter) => {
    if (set.size === list.length) setter(new Set());
    else setter(new Set(list.map((i) => i.id)));
  };

  const singleKasbon = async (id, status) => {
    try {
      await api.post(`/kasbon/${id}/approve`, { status });
      toast.success(status === "approved" ? "Disetujui" : "Ditolak");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const singlePO = async (id, status) => {
    try {
      await api.post(`/purchasing/${id}/approve`, { status });
      toast.success(status === "approved" ? "Disetujui" : "Ditolak");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const bulkKasbon = async (status) => {
    if (selKasbon.size === 0) return toast.warning("Pilih setidaknya 1 kasbon");
    try {
      const { data } = await api.post("/kasbon/bulk-approve", { ids: [...selKasbon], status });
      toast.success(`${data.modified} kasbon ${status === "approved" ? "disetujui" : "ditolak"}`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const bulkPO = async (status) => {
    if (selPO.size === 0) return toast.warning("Pilih setidaknya 1 PR/PO");
    try {
      const { data } = await api.post("/purchasing/bulk-approve", { ids: [...selPO], status });
      toast.success(`${data.modified} dokumen ${status === "approved" ? "disetujui" : "ditolak"}`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Approval Center" subtitle="Persetujuan kasbon dan purchase request/order (mendukung bulk)" testId="approval-header" />
      <Section>
        <Tabs defaultValue="kasbon">
          <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none h-auto">
            <TabsTrigger value="kasbon" data-testid="tab-approval-kasbon" className="rounded-none data-[state=active]:bg-[#FF6600] data-[state=active]:text-black font-mono text-xs uppercase">Kasbon ({data.kasbon.length})</TabsTrigger>
            <TabsTrigger value="po" data-testid="tab-approval-po" className="rounded-none data-[state=active]:bg-[#FF6600] data-[state=active]:text-black font-mono text-xs uppercase">Purchasing ({data.purchasing.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="kasbon" className="mt-4">
            {data.kasbon.length === 0 ? <Empty message="Tidak ada kasbon pending" /> : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className="font-mono text-[10px] uppercase text-neutral-500 mr-auto">
                    {selKasbon.size} dipilih dari {data.kasbon.length}
                  </div>
                  <Button onClick={() => bulkKasbon("approved")} data-testid="bulk-approve-kasbon" disabled={selKasbon.size === 0} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold">
                    <CheckSquare size={14} className="mr-1" /> Setujui Terpilih
                  </Button>
                  <Button onClick={() => bulkKasbon("rejected")} data-testid="bulk-reject-kasbon" disabled={selKasbon.size === 0} variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white">
                    <XSquare size={14} className="mr-1" /> Tolak Terpilih
                  </Button>
                </div>
                <div className="border border-neutral-800 bg-[#141414]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-800 hover:bg-transparent">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selKasbon.size === data.kasbon.length && data.kasbon.length > 0}
                            onCheckedChange={() => toggleAll(data.kasbon, selKasbon, setSelKasbon)}
                            data-testid="select-all-kasbon"
                          />
                        </TableHead>
                        <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tanggal</TableHead>
                        <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Jumlah</TableHead>
                        <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Alasan</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.kasbon.map((k) => (
                        <TableRow key={k.id} className="border-neutral-800" data-testid={`approval-kasbon-row-${k.id}`}>
                          <TableCell>
                            <Checkbox
                              checked={selKasbon.has(k.id)}
                              onCheckedChange={() => toggle(selKasbon, setSelKasbon, k.id)}
                              data-testid={`select-kasbon-${k.id}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs">{k.date}</TableCell>
                          <TableCell className="font-mono font-bold">{formatRupiah(k.amount)}</TableCell>
                          <TableCell className="font-mono text-xs">{k.reason}</TableCell>
                          <TableCell className="text-right space-x-2">
                            <button onClick={() => singleKasbon(k.id, "approved")} data-testid={`approve-kasbon-${k.id}`} className="text-[#16A34A]"><Check size={16} /></button>
                            <button onClick={() => singleKasbon(k.id, "rejected")} data-testid={`reject-kasbon-${k.id}`} className="text-[#DC2626]"><X size={16} /></button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="po" className="mt-4">
            {data.purchasing.length === 0 ? <Empty message="Tidak ada PR/PO pending" /> : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <div className="font-mono text-[10px] uppercase text-neutral-500 mr-auto">
                    {selPO.size} dipilih dari {data.purchasing.length}
                  </div>
                  <Button onClick={() => bulkPO("approved")} data-testid="bulk-approve-po" disabled={selPO.size === 0} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold">
                    <CheckSquare size={14} className="mr-1" /> Setujui Terpilih
                  </Button>
                  <Button onClick={() => bulkPO("rejected")} data-testid="bulk-reject-po" disabled={selPO.size === 0} variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white">
                    <XSquare size={14} className="mr-1" /> Tolak Terpilih
                  </Button>
                </div>
                <div className="border border-neutral-800 bg-[#141414]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-800 hover:bg-transparent">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selPO.size === data.purchasing.length && data.purchasing.length > 0}
                            onCheckedChange={() => toggleAll(data.purchasing, selPO, setSelPO)}
                            data-testid="select-all-po"
                          />
                        </TableHead>
                        <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tanggal</TableHead>
                        <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tipe</TableHead>
                        <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Supplier</TableHead>
                        <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.purchasing.map((p) => (
                        <TableRow key={p.id} className="border-neutral-800" data-testid={`approval-po-row-${p.id}`}>
                          <TableCell>
                            <Checkbox
                              checked={selPO.has(p.id)}
                              onCheckedChange={() => toggle(selPO, setSelPO, p.id)}
                              data-testid={`select-po-${p.id}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs">{p.date}</TableCell>
                          <TableCell className="font-mono text-xs">{p.type}</TableCell>
                          <TableCell className="font-mono text-xs">{p.supplier || "-"}</TableCell>
                          <TableCell className="font-mono font-bold">{formatRupiah(p.total)}</TableCell>
                          <TableCell className="text-right space-x-2">
                            <button onClick={() => singlePO(p.id, "approved")} data-testid={`approve-po-${p.id}`} className="text-[#16A34A]"><Check size={16} /></button>
                            <button onClick={() => singlePO(p.id, "rejected")} data-testid={`reject-po-${p.id}`} className="text-[#DC2626]"><X size={16} /></button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </Section>
    </div>
  );
}
